import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { compatApi } from '../../utils/Intercepter';
import useTokenStore from '../../stores/tokens';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';


const EVENT_TYPES = {
    HOLIDAY:     { label: '공휴일/국경일',  color: '#e35d6a' },
    ORG_EVENT:   { label: '조직 공유 일정', color: '#2563EB' },
    MAINTENANCE: { label: '유지보수',       color: '#EA580C' },
    PERSONAL:    { label: '개인 일정',      color: '#7C3AED' },
    PTO:         { label: '휴가/공가',      color: '#16A34A' },
};

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const EVENT_TYPE_ORDER = { HOLIDAY: 0, ORG_EVENT: 1, MAINTENANCE: 2, PERSONAL: 3, PTO: 4 };

function toISODateTime(value, isAllDay, endOfDay = false) {
    if (!value) return null;
    if (isAllDay) return endOfDay ? `${value}T23:59:59` : `${value}T00:00:00`;
    return value.length === 16 ? `${value}:00` : value;
}

function parseDateTime(val) {
    if (!val) return new Date(NaN);
    if (typeof val === 'string') return new Date(val);
    if (Array.isArray(val)) {
        const [y, mo, d, h = 0, mi = 0, s = 0] = val;
        return new Date(y, mo - 1, d, h, mi, s);
    }
    return new Date(val);
}

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

// ── Form atoms ──────────────────────────────────────────────────────────────
function SLabel({ children, required }) {
    return (
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', letterSpacing: '-0.005em', marginBottom: 6 }}>
            {children}{required && <span style={{ color: '#e35d6a', marginLeft: 4 }}>*</span>}
        </label>
    );
}
function SInput({ value, onChange, placeholder }) {
    const [focus, setFocus] = useState(false);
    return (
        <input type="text" value={value} onChange={e => onChange(e.target.value)}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            placeholder={placeholder}
            style={{
                width: '100%', height: 40, padding: '0 14px', borderRadius: 10, outline: 'none',
                border: `1.5px solid ${focus ? PURPLE : '#ececf2'}`,
                boxShadow: focus ? `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}` : 'none',
                background: 'white', fontSize: 13.5, color: '#1f1d2e', fontFamily: 'inherit',
                transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
            }}
        />
    );
}
function STextArea({ value, onChange, placeholder }) {
    const [focus, setFocus] = useState(false);
    return (
        <textarea value={value} onChange={e => onChange(e.target.value)}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            placeholder={placeholder} rows={3}
            style={{
                width: '100%', padding: '11px 14px', borderRadius: 10, outline: 'none',
                border: `1.5px solid ${focus ? PURPLE : '#ececf2'}`,
                boxShadow: focus ? `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}` : 'none',
                background: 'white', fontSize: 13.5, color: '#1f1d2e', fontFamily: 'inherit',
                resize: 'vertical', lineHeight: 1.55, minHeight: 70,
                transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
            }}
        />
    );
}
function SDateTime({ value, onChange, type }) {
    const [focus, setFocus] = useState(false);
    return (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            style={{
                width: '100%', height: 40, padding: '0 14px', borderRadius: 10, outline: 'none',
                border: `1.5px solid ${focus ? PURPLE : '#ececf2'}`,
                boxShadow: focus ? `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}` : 'none',
                background: 'white', color: '#1f1d2e', fontSize: 13.5, fontFamily: 'inherit',
                fontVariantNumeric: 'tabular-nums',
                transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
            }}
        />
    );
}

// ── Month grid ───────────────────────────────────────────────────────────────
function MonthGrid({ year, month, selectedDate, onSelectDate, events, filters }) {
    const today = new Date();
    const firstDow = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();

    const prevMonthLastDay = new Date(year, month - 1, 0).getDate();
    const cells = [];
    for (let i = firstDow - 1; i >= 0; i--) cells.push(new Date(year, month - 2, prevMonthLastDay - i));
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month - 1, d));
    let nextDay = 1;
    while (cells.length % 7 !== 0) cells.push(new Date(year, month, nextDay++));

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    const activeEvents = (events || [])
        .filter(ev => filters[ev.eventType])
        .sort((a, b) => (EVENT_TYPE_ORDER[a.eventType] ?? 3) - (EVENT_TYPE_ORDER[b.eventType] ?? 3))
        .map(ev => {
            const s = parseDateTime(ev.startDt);
            const e = parseDateTime(ev.endDt);
            return {
                ...ev,
                startDate: new Date(s.getFullYear(), s.getMonth(), s.getDate()),
                endDate: new Date(e.getFullYear(), e.getMonth(), e.getDate()),
            };
        })
        .filter(ev => !isNaN(ev.startDate) && !isNaN(ev.endDate));

    const EVENT_H = 20;
    const EVENT_GAP = 2;
    const DAY_NUM_H = 30;
    const INSET = 2;
    const MAX_ROWS = 3;

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f0eff5', background: '#fafaff' }}>
                {WEEKDAYS.map((wd, i) => (
                    <div key={wd} style={{ padding: '10px 0', textAlign: 'center', fontSize: 12, fontWeight: 600, color: i === 0 ? '#e35d6a' : i === 6 ? '#5d8fe0' : '#5d5b6e', letterSpacing: '.04em' }}>
                        {wd}
                    </div>
                ))}
            </div>

            {weeks.map((week, wi) => {
                const holidayDateSet = new Set();
                for (const ev of (events || [])) {
                    if (ev.eventType !== 'HOLIDAY') continue;
                    const s = parseDateTime(ev.startDt);
                    const e = parseDateTime(ev.endDt);
                    if (isNaN(s) || isNaN(e)) continue;
                    const cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
                    const endD = new Date(e.getFullYear(), e.getMonth(), e.getDate());
                    while (cur <= endD) {
                        holidayDateSet.add(`${cur.getFullYear()}-${cur.getMonth()}-${cur.getDate()}`);
                        cur.setDate(cur.getDate() + 1);
                    }
                }

                const multiItems = [];
                for (const ev of activeEvents.filter(ev => ev.eventType !== 'HOLIDAY')) {
                    let segStart = -1;
                    for (let c = 0; c < 7; c++) {
                        const d = new Date(week[c].getFullYear(), week[c].getMonth(), week[c].getDate());
                        const inEvent = d >= ev.startDate && d <= ev.endDate;
                        const dow = d.getDay();
                        const isWeekend = dow === 0 || dow === 6;
                        const isHol = holidayDateSet.has(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
                        const skipDay = ev.eventType === 'PTO' && (isWeekend || isHol);
                        if (inEvent && !skipDay) {
                            if (segStart === -1) segStart = c;
                        } else if (segStart !== -1) {
                            multiItems.push({ ev, startCol: segStart, endCol: c - 1, rowIdx: -1 });
                            segStart = -1;
                        }
                    }
                    if (segStart !== -1) {
                        multiItems.push({ ev, startCol: segStart, endCol: 6, rowIdx: -1 });
                    }
                }

                const slotRows = [];
                for (const item of multiItems) {
                    let placed = false;
                    for (let r = 0; r < slotRows.length; r++) {
                        if (slotRows[r].every(x => item.endCol < x.startCol || item.startCol > x.endCol)) {
                            slotRows[r].push(item);
                            item.rowIdx = r;
                            placed = true;
                            break;
                        }
                    }
                    if (!placed) {
                        if (slotRows.length < MAX_ROWS) {
                            item.rowIdx = slotRows.length;
                            slotRows.push([item]);
                        } else {
                            item.rowIdx = -1;
                        }
                    }
                }

                const colTotalCount = Array(7).fill(0);
                for (const item of multiItems) {
                    for (let c = item.startCol; c <= item.endCol; c++) {
                        colTotalCount[c]++;
                    }
                }
                const colHasOverflow = colTotalCount.map(count => count > MAX_ROWS);
                const hasAnyOverflow = colHasOverflow.some(Boolean);
                const numMultiRows = slotRows.length + (hasAnyOverflow ? 1 : 0);
                const cellHeight = Math.max(100, DAY_NUM_H + numMultiRows * (EVENT_H + EVENT_GAP) + 10);
                const isLastWeek = wi === weeks.length - 1;

                return (
                    <div key={wi} style={{ position: 'relative', borderBottom: isLastWeek ? 'none' : '1px solid #f0eff5' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                            {week.map((date, di) => {
                                const inMonth = date.getMonth() === month - 1;
                                const nd = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                const dayEvs = activeEvents.filter(ev => nd >= ev.startDate && nd <= ev.endDate);
                                const holidayEv = dayEvs.find(ev => ev.eventType === 'HOLIDAY');
                                const isSelected = sameDay(date, selectedDate);
                                const isToday = sameDay(date, today);
                                const isSun = di === 0;
                                const isSat = di === 6;

                                let dateColor = inMonth ? '#3a3849' : '#c4c3d0';
                                if (inMonth && (holidayEv || isSun)) dateColor = '#e35d6a';
                                else if (!inMonth && (holidayEv || isSun)) dateColor = '#e8a5ac';
                                else if (inMonth && isSat) dateColor = '#5d8fe0';
                                else if (!inMonth && isSat) dateColor = '#a5bde0';

                                return (
                                    <button key={di} onClick={() => onSelectDate(date)}
                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f4f3f9'; }}
                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? hexToRgba(PURPLE, 0.05) : inMonth ? 'white' : '#fafafa'; }}
                                        style={{
                                            height: cellHeight, outline: 'none',
                                            border: 'none',
                                            borderLeft: di === 0 ? 'none' : '1px solid #f4f3f9',
                                            backgroundColor: isSelected ? '#FCE7F3' : isToday ? hexToRgba(PURPLE, 0.05) : inMonth ? 'white' : '#fafafa',
                                            cursor: 'pointer', boxSizing: 'border-box', padding: '6px 6px 4px',
                                            textAlign: 'left', fontFamily: 'inherit',
                                            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%',
                                            transition: 'background 120ms',
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 4,
                                            fontSize: 12.5, fontWeight: isToday ? 700 : 500,
                                            color: isSelected ? '#9D174D' : dateColor,
                                            lineHeight: 1,
                                        }}>
                                            {isToday && !isSelected ? (
                                                <span style={{
                                                    width: 22, height: 22, borderRadius: '50%',
                                                    background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                                    color: 'white', fontSize: 11.5, fontWeight: 700,
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>{date.getDate()}</span>
                                            ) : (
                                                <span>{date.getDate()}</span>
                                            )}
                                            {holidayEv && (
                                                <span style={{
                                                    fontSize: 10.5, fontWeight: 500,
                                                    color: isSelected ? '#be185d' : '#e35d6a',
                                                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 72,
                                                }}>{holidayEv.title}</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {multiItems.filter(item => item.rowIdx >= 0).map(({ ev, startCol, endCol, rowIdx }, idx) => {
                            const color = ev.colorCode || EVENT_TYPES[ev.eventType]?.color || '#888';
                            const top = DAY_NUM_H + rowIdx * (EVENT_H + EVENT_GAP);
                            const isStart = week[startCol] && new Date(week[startCol].getFullYear(), week[startCol].getMonth(), week[startCol].getDate()).getTime() === ev.startDate.getTime();
                            const isEnd = week[endCol] && new Date(week[endCol].getFullYear(), week[endCol].getMonth(), week[endCol].getDate()).getTime() === ev.endDate.getTime();
                            return (
                                <div key={idx} style={{
                                    position: 'absolute', top,
                                    left: `calc(${(startCol / 7) * 100}% + ${isStart ? INSET + 1 : 0}px)`,
                                    width: `calc(${((endCol - startCol + 1) / 7) * 100}% - ${(isStart ? INSET + 1 : 0) + (isEnd ? INSET + 1 : 0)}px)`,
                                    height: EVENT_H, backgroundColor: color,
                                    borderRadius: `${isStart ? 4 : 0}px ${isEnd ? 4 : 0}px ${isEnd ? 4 : 0}px ${isStart ? 4 : 0}px`,
                                    display: 'flex', alignItems: 'center',
                                    paddingLeft: isStart ? 7 : 4, paddingRight: 4,
                                    boxSizing: 'border-box', pointerEvents: 'none', zIndex: 1,
                                    boxShadow: '0 1px 2px rgba(20,15,40,.08)',
                                }}>
                                    {isStart && (
                                        <span style={{ fontSize: 11, color: '#fff', fontWeight: 600, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', letterSpacing: '-0.005em' }}>
                                            {ev.title}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {hasAnyOverflow && colHasOverflow.map((hasOv, c) => {
                            if (!hasOv) return null;
                            const top = DAY_NUM_H + slotRows.length * (EVENT_H + EVENT_GAP);
                            return (
                                <div key={`ov-${c}`} style={{
                                    position: 'absolute', top,
                                    left: `calc(${(c / 7) * 100}% + 4px)`,
                                    width: `calc(${(1 / 7) * 100}% - 8px)`,
                                    height: EVENT_H,
                                    display: 'flex', alignItems: 'center',
                                    paddingLeft: 4,
                                    pointerEvents: 'none', zIndex: 1,
                                }}>
                                    <span style={{ fontSize: 11, color: '#5d5b6e', fontWeight: 600, letterSpacing: '-0.01em' }}>
                                        +{colTotalCount[c] - MAX_ROWS}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

// ── Year/Month Picker Popup ──────────────────────────────────────────────────
const PICKER_MONTHS = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

function YearMonthPickerPopup({ year, month, onConfirm, onClose }) {
    const [selYear,  setSelYear]  = useState(year);
    const [selMonth, setSelMonth] = useState(month);

    useEffect(() => { setSelYear(year); setSelMonth(month); }, [year, month]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(15, 10, 40, 0.40)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'white', borderRadius: 18,
                    padding: '22px 20px 18px',
                    width: 280,
                    boxShadow: '0 8px 40px -8px rgba(30,20,80,.22), 0 2px 8px rgba(30,20,80,.08)',
                }}
            >
                {/* 연도 선택 */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <button
                        onClick={() => setSelYear(y => y - 1)}
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #ececf2', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5d5b6e' }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <span style={{ fontSize: 17, fontWeight: 800, color: '#1f1d2e', letterSpacing: '-0.02em' }}>{selYear}년</span>
                    <button
                        onClick={() => setSelYear(y => y + 1)}
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #ececf2', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5d5b6e' }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </div>

                {/* 월 그리드 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, marginBottom: 18 }}>
                    {PICKER_MONTHS.map((label, idx) => {
                        const m = idx + 1;
                        const selected = m === selMonth;
                        return (
                            <button
                                key={m}
                                onClick={() => setSelMonth(m)}
                                style={{
                                    padding: '9px 4px', borderRadius: 9, border: 'none', cursor: 'pointer',
                                    background: selected ? PURPLE : '#f4f1fa',
                                    color: selected ? 'white' : '#3a3849',
                                    fontSize: 13, fontWeight: selected ? 700 : 500,
                                    fontFamily: 'inherit',
                                    transition: 'background 120ms, color 120ms',
                                }}
                            >{label}</button>
                        );
                    })}
                </div>

                {/* 확인 버튼 */}
                <button
                    onClick={() => onConfirm(selYear, selMonth)}
                    style={{
                        width: '100%', padding: '11px 0', borderRadius: 11, border: 'none',
                        background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                        color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        fontFamily: 'inherit',
                        boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.5)}`,
                    }}
                >확인</button>
            </div>
        </div>
    );
}

// ── Main page ────────────────────────────────────────────────────────────────
const initNow = new Date();

export default function CalendarPage() {
    const { user } = useTokenStore();
    const [viewYear, setViewYear] = useState(initNow.getFullYear());
    const [viewMonth, setViewMonth] = useState(initNow.getMonth() + 1);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [filters, setFilters] = useState({ HOLIDAY: true, ORG_EVENT: true, MAINTENANCE: true, PERSONAL: true, PTO: true });
    const [form, setForm] = useState({ title: '', description: '', eventType: 'PERSONAL', startDt: '', endDt: '', isAllDay: true });
    const [typeOpen, setTypeOpen] = useState(false);

    const calendarRef = useRef(null);
    const [calendarHeight, setCalendarHeight] = useState(0);

    useEffect(() => {
        if (!calendarRef.current) return;

        const observer = new ResizeObserver(entries => {
            setCalendarHeight(entries[0].contentRect.height);
        });

        observer.observe(calendarRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        compatApi.get('/calendar/events', { params: { year: viewYear, month: viewMonth, organizationCode: user?.currentOrganizationCode } })
            .then(res => { if (!cancelled) setEvents(res.data || []); })
            .catch(() => { if (!cancelled) setEvents([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [viewYear, viewMonth, user?.currentOrganizationCode]);

    const refetchCurrent = useCallback(() => {
        setLoading(true);
        compatApi.get('/calendar/events', { params: { year: viewYear, month: viewMonth, organizationCode: user?.currentOrganizationCode } })
            .then(res => setEvents(res.data || []))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [viewYear, viewMonth, user?.currentOrganizationCode]);

    const goToPrev = () => {
        setViewYear(y => viewMonth === 1 ? y - 1 : y);
        setViewMonth(m => m === 1 ? 12 : m - 1);
    };
    const goToNext = () => {
        setViewYear(y => viewMonth === 12 ? y + 1 : y);
        setViewMonth(m => m === 12 ? 1 : m + 1);
    };
    const goToToday = () => {
        const n = new Date();
        setViewYear(n.getFullYear());
        setViewMonth(n.getMonth() + 1);
        setSelectedDate(n);
    };

    const getEventsForDate = useCallback((date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dow = d.getDay();
        const isWeekend = dow === 0 || dow === 6;
        const isHoliday = events.some(ev => {
            if (ev.eventType !== 'HOLIDAY') return false;
            const s = parseDateTime(ev.startDt);
            const e = parseDateTime(ev.endDt);
            if (isNaN(s) || isNaN(e)) return false;
            const start = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            return d >= start && d <= end;
        });
        return events.filter(ev => {
            if (!filters[ev.eventType]) return false;
            if (ev.eventType === 'PTO' && (isWeekend || isHoliday)) return false;
            const s = parseDateTime(ev.startDt);
            const e = parseDateTime(ev.endDt);
            if (isNaN(s) || isNaN(e)) return false;
            const start = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            return d >= start && d <= end;
        }).sort((a, b) => (EVENT_TYPE_ORDER[a.eventType] ?? 3) - (EVENT_TYPE_ORDER[b.eventType] ?? 3));
    }, [events, filters]);

    const selectedDateEvents = getEventsForDate(selectedDate);

    const openCreateModal = () => {
        const y = selectedDate.getFullYear();
        const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const d = String(selectedDate.getDate()).padStart(2, '0');
        setForm({ title: '', description: '', eventType: 'PERSONAL', startDt: `${y}-${m}-${d}`, endDt: '', isAllDay: true });
        setTypeOpen(false);
        setShowModal(true);
    };

    const handleCreateEvent = async () => {
        if (!form.title.trim()) { toast.warn('제목을 입력하세요.', toastConfig); return; }
        if (!form.startDt) { toast.warn('시작일을 입력하세요.', toastConfig); return; }
        const effectiveEndDt = form.endDt || form.startDt;
        setSubmitting(true);
        try {
            await compatApi.post('/calendar/events', {
                eventType: form.eventType,
                organizationCode: ['ORG_EVENT', 'MAINTENANCE'].includes(form.eventType) ? user.currentOrganizationCode : null,
                title: form.title,
                description: form.description,
                startDt: toISODateTime(form.startDt, form.isAllDay, false),
                endDt: toISODateTime(effectiveEndDt, form.isAllDay, true),
                isAllDay: form.isAllDay,
                colorCode: EVENT_TYPES[form.eventType]?.color,
            });
            toast.success('일정이 등록되었습니다.', toastConfig);
            setShowModal(false);
            refetchCurrent();
        } catch {
            toast.error('일정 등록에 실패했습니다.', toastConfig);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;
        try {
            await compatApi.del(`/calendar/events/${eventId}`, null);
            toast.success('일정이 삭제되었습니다.', toastConfig);
            refetchCurrent();
        } catch {
            toast.error('일정 삭제에 실패했습니다.', toastConfig);
        }
    };

    const MONTH_NAMES = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const weekday = WEEKDAYS[selectedDate.getDay()];
    const typeOpts = [
        { value: 'PERSONAL', label: '개인 일정' },
        ...(user?.currentOrganizationApproved ? [
            { value: 'ORG_EVENT',   label: '조직 공유 일정' },
            { value: 'MAINTENANCE', label: '유지보수' },
        ] : []),
    ];

    const closeModal = () => { setShowModal(false); setTypeOpen(false); };

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
                {/* 페이지 헤더 */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>일정</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>일정 관리</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>일정 관리</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7a7989' }}>
                                개인·조직 일정과 휴가, 공휴일을 한눈에 확인하고 관리합니다.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={refetchCurrent} disabled={loading} title="새로 고침"
                                style={{
                                    height: 38, width: 38, borderRadius: 10,
                                    background: 'white', border: '1px solid #ececf2',
                                    color: '#5d5b6e', cursor: loading ? 'not-allowed' : 'pointer', padding: 0,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    opacity: loading ? 0.6 : 1,
                                }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                            </button>
                            {user?.currentOrganizationApproved && (
                                <button onClick={openCreateModal} style={{
                                    height: 38, padding: '0 18px', borderRadius: 10,
                                    background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                    border: 'none', color: 'white', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                    boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.55)}`,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    일정 등록
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 범례 */}
                <div style={{
                    display: 'flex', gap: 18, flexWrap: 'wrap',
                    padding: '10px 16px', marginBottom: 16,
                    background: 'white', border: '1px solid #f0eff5', borderRadius: 12,
                    boxShadow: '0 1px 2px rgba(60,50,120,.03)',
                }}>
                    {Object.entries(EVENT_TYPES).map(([type, { label, color }]) => (
                        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setFilters(f => ({ ...f, [type]: !f[type] }))}>
                            <span style={{
                                width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                                background: filters[type] ? color : 'transparent',
                                border: `2px solid ${color}`,
                                transition: 'background 140ms',
                            }} />
                            <span style={{ fontSize: 12.5, color: filters[type] ? '#3a3849' : '#aaa9b7', fontWeight: 500, transition: 'color 140ms' }}>
                                {label}
                            </span>
                        </div>
                    ))}
                </div>

                {/* 달력 + 사이드 패널 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16, alignItems: 'stretch' }}>
                    {/* 달력 카드 */}
                    <div 
                        ref={calendarRef}
                        style={{
                        background: 'white', borderRadius: 14,
                        border: '1px solid #f0eff5',
                        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center',
                            padding: '14px 18px', gap: 12,
                            borderBottom: '1px solid #f0eff5',
                            background: 'linear-gradient(180deg, #faf9fd 0%, white 100%)',
                        }}>
                            <button onClick={goToPrev} title="이전 달" style={{
                                width: 30, height: 30, borderRadius: 8,
                                background: 'white', border: '1px solid #ececf2',
                                color: '#5d5b6e', cursor: 'pointer', padding: 0,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <button
                                    onClick={() => setPickerOpen(true)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
                                        borderRadius: 8, fontFamily: 'inherit',
                                        transition: 'background 120ms',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f4f1fa'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                >
                                    <span style={{ fontWeight: 700, fontSize: 16, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                        {viewYear}년 {MONTH_NAMES[viewMonth - 1]}
                                    </span>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9892b0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                                {loading && <span style={{ color: '#8b8a99', fontSize: 11 }}>업데이트 중...</span>}
                            </div>
                            {pickerOpen && (
                                <YearMonthPickerPopup
                                    year={viewYear}
                                    month={viewMonth}
                                    onConfirm={(y, m) => { setViewYear(y); setViewMonth(m); setPickerOpen(false); }}
                                    onClose={() => setPickerOpen(false)}
                                />
                            )}
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={goToToday} style={{
                                    height: 30, padding: '0 12px', borderRadius: 8,
                                    background: 'white', border: '1px solid #ececf2',
                                    color: '#3a3849', fontSize: 12, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                }}>오늘</button>
                                <button onClick={goToNext} title="다음 달" style={{
                                    width: 30, height: 30, borderRadius: 8,
                                    background: 'white', border: '1px solid #ececf2',
                                    color: '#5d5b6e', cursor: 'pointer', padding: 0,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <MonthGrid
                            year={viewYear} month={viewMonth}
                            selectedDate={selectedDate} onSelectDate={setSelectedDate}
                            events={events} filters={filters}
                        />
                    </div>

                    {/* 선택 날짜 패널 */}
                    <div style={{
                        background: 'white', borderRadius: 14,
                        border: '1px solid #f0eff5',
                        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                        overflow: 'hidden',
                    }}>
                        <div style={{ padding: '16px 18px', borderBottom: '1px solid #f0eff5', background: 'linear-gradient(180deg, #faf9fd 0%, white 100%)' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                                <div>
                                    <div style={{ fontSize: 10.5, color: '#8b8a99', fontWeight: 700, letterSpacing: '.08em', marginBottom: 3 }}>SELECTED DATE</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                        {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일{' '}
                                        <span style={{
                                            fontSize: 13, fontWeight: 600,
                                            color: selectedDate.getDay() === 0 ? '#e35d6a' : selectedDate.getDay() === 6 ? '#5d8fe0' : '#5d5b6e',
                                        }}>{weekday}</span>
                                    </div>
                                </div>
                                <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    background: hexToRgba(PURPLE, 0.12), color: PURPLE,
                                    padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                                }}>{selectedDateEvents.length}건</span>
                            </div>
                        </div>

                        <div style={{ overflowY: 'auto', maxHeight: calendarHeight-100, padding: '12px 12px 8px' }}>
                            {selectedDateEvents.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                                    <div style={{
                                        width: 44, height: 44, borderRadius: 12,
                                        background: hexToRgba(MINT, 0.12), color: MINT,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        marginBottom: 10,
                                    }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </div>
                                    <div style={{ fontSize: 13, color: '#5d5b6e', fontWeight: 600, marginBottom: 4 }}>등록된 일정이 없습니다</div>
                                    <div style={{ fontSize: 12, color: '#aaa9b7', marginBottom: 14 }}>이 날짜에 새 일정을 등록해 보세요.</div>
                                    {user?.currentOrganizationApproved && (
                                        <button onClick={openCreateModal} style={{
                                            height: 34, padding: '0 14px', borderRadius: 9,
                                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                            border: 'none', color: 'white', fontSize: 12.5, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: 'inherit',
                                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.55)}`,
                                        }}>+ 일정 등록</button>
                                    )}
                                </div>
                            ) : selectedDateEvents.map((ev, i) => {
                                const color = ev.colorCode || EVENT_TYPES[ev.eventType]?.color || '#888';
                                const t = EVENT_TYPES[ev.eventType];
                                const isOwner = ev.creatorId === user?.userEmail;
                                return (
                                    <div key={ev.eventId || i} style={{
                                        position: 'relative',
                                        background: hexToRgba(color, 0.04),
                                        border: `1px solid ${hexToRgba(color, 0.18)}`,
                                        borderLeft: `4px solid ${color}`,
                                        borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.005em', marginBottom: 4, lineHeight: 1.4, wordBreak: 'break-word' }}>
                                                    {ev.title}
                                                </div>
                                                {!ev.isAllDay && (() => {
                                                    const s = parseDateTime(ev.startDt);
                                                    const e = parseDateTime(ev.endDt);
                                                    const fmt = d => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                                    const timeStr = ev.endDt ? `${fmt(s)} ~ ${fmt(e)}` : fmt(s);
                                                    return (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#7b79a1', marginBottom: 3 }}>
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                                            </svg>
                                                            {timeStr}
                                                        </div>
                                                    );
                                                })()}
                                                {ev.description && ev.description !== '공공데이터 자동 동기화' && (
                                                    <div style={{ fontSize: 12, color: '#5d5b6e', lineHeight: 1.5, marginBottom: 4, wordBreak: 'break-word' }}>
                                                        {ev.description}
                                                    </div>
                                                )}
                                                {ev.eventType !== 'HOLIDAY' && ev.creatorId && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: '#8b8a99', marginBottom: 4 }}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                                                        </svg>
                                                        {ev.creatorName || ev.creatorId}
                                                    </div>
                                                )}
                                                <div style={{ marginTop: 6 }}>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                                        fontSize: 10.5, fontWeight: 700, color: 'white',
                                                        background: color, padding: '3px 8px', borderRadius: 5,
                                                    }}>{t?.label || ev.eventType}</span>
                                                </div>
                                            </div>
                                            {ev.eventType !== 'HOLIDAY' && isOwner && (
                                                <button onClick={() => handleDeleteEvent(ev.eventId)}
                                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(196,69,105,.08)'; e.currentTarget.style.borderColor = hexToRgba('#c44569', 0.4); }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#ececf2'; }}
                                                    style={{
                                                        height: 24, padding: '0 9px', borderRadius: 6,
                                                        background: 'white', border: '1px solid #ececf2',
                                                        color: '#c44569', fontSize: 11, fontWeight: 600,
                                                        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                                                    }}>삭제</button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 일정 등록 모달 */}
                {showModal && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 1050,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                        background: 'rgba(28,26,46,.45)', backdropFilter: 'blur(3px)',
                        WebkitBackdropFilter: 'blur(3px)',
                    }}>
                        <div onClick={closeModal} style={{ position: 'absolute', inset: 0 }} />
                        <div style={{
                            position: 'relative', zIndex: 1,
                            width: 'min(500px, 100%)', maxHeight: 'calc(100vh - 48px)',
                            background: 'white', borderRadius: 18,
                            boxShadow: '0 30px 60px -20px rgba(20,15,40,.4), 0 8px 22px -10px rgba(20,15,40,.25)',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}>
                            {/* 모달 헤더 */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #ececf2' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 34, height: 34, borderRadius: 10,
                                        background: `linear-gradient(135deg, ${hexToRgba(PURPLE, 0.15)}, ${hexToRgba(MINT, 0.18)})`,
                                        color: PURPLE,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 15.5, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>일정 등록</div>
                                        <div style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>일정 유형과 일시를 입력해 새로운 일정을 추가하세요.</div>
                                    </div>
                                </div>
                                <button onClick={closeModal}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f4f3f9'; e.currentTarget.style.color = '#1f1d2e'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8b8a99'; }}
                                    style={{
                                        width: 34, height: 34, borderRadius: 9,
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        color: '#8b8a99', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>

                            {/* 모달 바디 */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px 8px', background: '#fafaff' }}>
                                <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #ececf2', boxShadow: '0 1px 2px rgba(60,50,120,.03)' }}>
                                    {/* 일정 유형 */}
                                    <div style={{ marginBottom: 16 }}>
                                        <SLabel>일정 유형</SLabel>
                                        <div style={{ position: 'relative' }}>
                                            <button type="button" onClick={() => setTypeOpen(o => !o)}
                                                style={{
                                                    width: '100%', height: 40, padding: '0 36px 0 14px',
                                                    borderRadius: 10, outline: 'none',
                                                    border: `1.5px solid ${typeOpen ? PURPLE : '#ececf2'}`,
                                                    boxShadow: typeOpen ? `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}` : 'none',
                                                    background: 'white', textAlign: 'left', fontSize: 13.5, color: '#1f1d2e',
                                                    fontFamily: 'inherit', cursor: 'pointer', position: 'relative',
                                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                                    transition: 'border-color 140ms, box-shadow 140ms',
                                                }}>
                                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: EVENT_TYPES[form.eventType]?.color, flexShrink: 0 }} />
                                                <span style={{ fontWeight: 500, flex: 1 }}>{typeOpts.find(o => o.value === form.eventType)?.label}</span>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                    style={{ position: 'absolute', right: 12, top: '50%', transform: `translateY(-50%) ${typeOpen ? 'rotate(180deg)' : ''}`, transition: 'transform 160ms' }}>
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>
                                            {typeOpen && (
                                                <>
                                                    <div onClick={() => setTypeOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                                                    <div style={{
                                                        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 6,
                                                        background: 'white', borderRadius: 10,
                                                        boxShadow: '0 12px 28px -8px rgba(60,50,120,.22)',
                                                        border: '1px solid #ececf2', padding: 4,
                                                    }}>
                                                        {typeOpts.map(o => {
                                                            const active = o.value === form.eventType;
                                                            return (
                                                                <button key={o.value} type="button"
                                                                    onClick={() => { setForm(f => ({ ...f, eventType: o.value })); setTypeOpen(false); }}
                                                                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#fafaff'; }}
                                                                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                                                    style={{
                                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                                        width: '100%', padding: '9px 12px', borderRadius: 7,
                                                                        background: active ? hexToRgba(PURPLE, 0.08) : 'transparent',
                                                                        border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                                                        color: active ? PURPLE : '#3a3849',
                                                                        fontSize: 13, fontWeight: active ? 600 : 500, textAlign: 'left',
                                                                    }}>
                                                                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: EVENT_TYPES[o.value]?.color, flexShrink: 0 }} />
                                                                    {o.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* 제목 */}
                                    <div style={{ marginBottom: 16 }}>
                                        <SLabel required>제목</SLabel>
                                        <SInput value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="일정 제목을 입력하세요" />
                                    </div>

                                    {/* 설명 */}
                                    <div style={{ marginBottom: 16 }}>
                                        <SLabel>설명</SLabel>
                                        <STextArea value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="설명 (선택)" />
                                    </div>

                                    {/* 하루 종일 */}
                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: '#3a3849', fontWeight: 500 }}>
                                            <span style={{
                                                width: 18, height: 18, borderRadius: 5,
                                                border: `1.5px solid ${form.isAllDay ? PURPLE : '#d4d3df'}`,
                                                background: form.isAllDay ? PURPLE : 'white',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0, transition: 'all 140ms',
                                            }}>
                                                {form.isAllDay && (
                                                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                                                        <path d="M3 7l3 3 5-6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                            <input type="checkbox" checked={form.isAllDay} style={{ display: 'none' }}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    setForm(f => {
                                                        const dateOnly = val => val ? val.substring(0, 10) : '';
                                                        if (!checked) {
                                                            const now = new Date();
                                                            const hh = String(now.getHours()).padStart(2, '0');
                                                            const mi = String(now.getMinutes()).padStart(2, '0');
                                                            return { ...f, isAllDay: false, startDt: `${dateOnly(f.startDt)}T${hh}:${mi}`, endDt: f.endDt ? `${dateOnly(f.endDt)}T${hh}:${mi}` : '' };
                                                        }
                                                        return { ...f, isAllDay: true, startDt: dateOnly(f.startDt), endDt: dateOnly(f.endDt) };
                                                    });
                                                }}
                                            />
                                            하루 종일
                                        </label>
                                    </div>

                                    {/* 시작 / 종료 */}
                                    <div style={{ display: 'grid', gridTemplateColumns: form.isAllDay ? '1fr 1fr' : '1fr', gap: 12 }}>
                                        <div>
                                            <SLabel required>시작</SLabel>
                                            <SDateTime value={form.startDt} onChange={v => setForm(f => ({ ...f, startDt: v }))} type={form.isAllDay ? 'date' : 'datetime-local'} />
                                        </div>
                                        {form.isAllDay ? (
                                            <div>
                                                <SLabel>종료</SLabel>
                                                <SDateTime value={form.endDt} onChange={v => setForm(f => ({ ...f, endDt: v }))} type="date" />
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 12 }}>
                                                <SLabel>종료</SLabel>
                                                <SDateTime value={form.endDt} onChange={v => setForm(f => ({ ...f, endDt: v }))} type="datetime-local" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 모달 푸터 */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '14px 22px', borderTop: '1px solid #ececf2', background: 'white' }}>
                                <button onClick={closeModal} style={{
                                    height: 38, padding: '0 16px', borderRadius: 9,
                                    background: 'white', border: '1.5px solid #ececf2',
                                    color: '#5d5b6e', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                }}>취소</button>
                                <button onClick={handleCreateEvent} disabled={submitting || !form.title.trim()} style={{
                                    height: 38, padding: '0 22px', borderRadius: 9, border: 'none',
                                    background: (form.title.trim() && !submitting) ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : '#d4d3df',
                                    color: 'white', fontSize: 13, fontWeight: 600,
                                    cursor: (form.title.trim() && !submitting) ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                                    boxShadow: (form.title.trim() && !submitting) ? `0 8px 16px -8px ${hexToRgba(PURPLE, 0.6)}` : 'none',
                                    transition: 'background 160ms',
                                }}>{submitting ? '등록 중...' : '등록'}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
